// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei } from './conversions'
import { PoolTypeEnum, PoolTypeToString, yupErc20Wei, yupEthAddr } from './Enums'
import { EthAddr, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

export const PoolBaseConfigurationCTOSchemaObjectValues: Record<string, yup.Schema> = {
  id: yup
    .string()
    .label('PoolId poolAddr')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of pool ', stringType: StringTypeEnum.ID })),
  poolType: yup
    .number()
    .label('Pool Type')
    .required()
    .meta(toFieldMetaData({ propName: 'poolType', description: 'Type of pool ', stateToString: PoolTypeToString })),

  poolAddr: yupEthAddr.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',
      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  liquidityAssetAddr: yupEthAddr.label('Liquidity Asset Address').meta(
    toFieldMetaData({
      propName: 'liquidityAssetAddr',
      description: 'The smart-contract address for the liquidity asset e.g. USDC, EURC. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  poolControllerAddr: yupEthAddr.label('Vault Controller Address').meta(
    toFieldMetaData({
      propName: 'poolControllerAddr',
      stringType: StringTypeEnum.ETH_ADDR,
      description: 'The smart-contract address for the Vault Controller. ',
    })
  ),

  name: yup
    .string()
    .required()
    .label('Vault Name')
    .meta(toFieldMetaData({ propName: 'name', description: 'Name of the Loan Vault', example: 'TokenName' })),
  symbol: yup
    .string()
    .required()
    .label('Token Symbol')
    .meta(toFieldMetaData({ propName: 'symbol', description: 'Symbol for Vault Tokens', example: 'OTT' })),

  borrowerManagerAddr: yupEthAddr.label('Borrower Manager Address').meta(
    toFieldMetaData({
      propName: 'borrowerManagerAddr',
      description: 'The wallet address of the Advisor.',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),

  poolAdminAddr: yupEthAddr.label('Pool Administrator Address').meta(
    toFieldMetaData({
      propName: 'poolAdminAddr',
      description: 'The wallet address of the Pool Administrator',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),

  borrowerWalletAddr: yupEthAddr.label('Borrower Wallet Address').meta(
    toFieldMetaData({
      propName: 'borrowerWalletAddr',
      description: 'The wallet address of the Borrower. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  chainConfigurationName: yup
    .string()
    .required()
    .label('chainConfigurationName')
    .meta(
      toFieldMetaData({
        propName: 'chainConfigurationName',
        description: 'chainConfigurationName',
      })
    ),
  creationBlock: yup
    .number()
    .required()
    .label('Creation Block Number')
    .meta(
      toFieldMetaData({
        propName: 'creationBlock',
        description: 'The block number when the pool was created',
      })
    ),
  //
}

export const PoolBaseConfigurationCTOSchema = yup
  .object(PoolBaseConfigurationCTOSchemaObjectValues)
  .label('PoolBaseConfigurationCTO')

export interface PoolBaseConfigurationCTO extends InferType<typeof PoolBaseConfigurationCTOSchema> {
  poolType: PoolTypeEnum
  poolAddr: EthAddr
  liquidityAssetAddr: EthAddr
  poolAdminAddr: EthAddr
  poolControllerAddr: EthAddr

  borrowerManagerAddr: EthAddr
  borrowerWalletAddr: EthAddr

  liquidityTokenSymbol: string
  currencyLabel: string
  chainConfigurationName: string // TODO: #860 Change to ChainConfigName?
  creationBlock: number
  //feeVaultAddr: EthAddr
  // withdrawControllerAddr: EthAddr
  //borrowerVaultAddr: EthAddr
  //maxCapacity: ERC20Wei
}

export const PoolBaseOverviewCTOSchemaValues: Record<string, yup.Schema> = {
  blockNumber: yup
    .number()
    .label('Block Number')
    .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block number of the data' })),
  timestamp: yup
    .number()
    .label('Timestamp')
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        description: 'Timestamp of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME,
      })
    ),
  dayNumber: yup
    .number()
    .label('Day Number')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'dayNumber',
        description: 'day number of timestamp',
        stringType: StringTypeEnum.ETH_DAY_NUMBER,
      })
    ),
  timeOfDay: yup
    .number()
    .label('Time of Day')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timeOfDay',
        description: 'Time Stamp of the data',
        stringType: StringTypeEnum.ETH_TIME_OF_DAY,
      })
    ),

  chainId: yup
    .number()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', example: '5', description: 'ChainId of the Data' })),

  totalOutstandingLoanPrincipal: yupErc20Wei.label('Total Outstanding Loan Principal').meta(
    toFieldMetaData({
      propName: 'totalOutstandingLoanPrincipal',
      description:
        'The total outstanding loan principal for the Loan Vault. Calculated as: totalPrincipalEarningInterest + totalAssetsTransitioningIn + totalAssetsDueForWithdraws. Uses 6 as a decimal value, so display value should be divided by 1000000. For example, 1000000 = 1.00',
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}
export const PoolBaseOverviewCTOSchema = yup.object({
  ...PoolBaseOverviewCTOSchemaValues,
  ...PoolBaseConfigurationCTOSchemaObjectValues,
})

export interface PoolBaseOverviewCTO extends InferType<typeof PoolBaseOverviewCTOSchema>, PoolBaseConfigurationCTO {
  blockNumber: number
  timestamp: number
  timeOfDay: number
  dayNumber: number
  chainId: number
  totalOutstandingLoanPrincipal: ERC20Wei
}
